/**
 * SamConversationBase - Refactored checkpoint conversation component
 *
 * This component has been refactored to use:
 * - useConversationState: State machine for conversation flow
 * - useCheckpointTimer: Timer for close button visibility
 * - usePhotoCapture: Photo capture handling
 * - useLiveKitSync: LiveKit attribute synchronization
 *
 * The component maintains backward compatibility with existing parents:
 * - SamCheckpointOverlay (isOverlay=true)
 * - PreCallScreen (isOverlay=false, with renderCustomButtons)
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useCamera } from "@common/hooks/useCamera"
import { useRequestsBatch } from "@common/hooks/useRequestsBatch"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { formIds } from "@data/constants/formConstants"
import { apiRequest } from "@data/redux/actions/api"
import { setCheckpointConversation, setQuizProgress, clearQuizProgress } from "@data/redux/actions/liveMode"
import { liveService } from "@data/services/liveService"
import { activitiesService } from "@data/services/activitiesService"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import { useNavigation } from "@react-navigation/native"
import React, { useContext, useEffect, useCallback, useMemo, useRef, useState } from "react"
import { StyleSheet, View } from "react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import Animated, { FadeOut } from "react-native-reanimated"
import { connect, useSelector } from "react-redux"
import { getMostRecentActivity } from "../../data/utility/dataUtils"
import { useCurrentActivity } from "../hooks/useCurrentActivity"
import moment from "moment"

// Import refactored modules
import {
    useConversationState,
    useCheckpointTimer,
    usePhotoCapture,
    useLiveKitSync,
    isStartingBlockContext,
    isEndedStatus,
    hasValidMessageBody,
    isEmptyCheckpointContent,
    isQuizPendingWithoutQuestion,
    RESPONSE_ACTIONS,
    TIMING
} from "./checkpoint"

import {
    CheckpointHeader,
    ConversationBubble,
    ResponseOptions,
    FeedbackButtons
} from "./checkpoint/components"

import QuizQuestionSignalButton from "../../quiz/components/QuizQuestionSignalButton"
import QuizCorrectionReview from "../../student/activity/execute/QuizCorrectionReview"

// Shown in the overlay when a conversation has nothing to display (missing
// agent_message, empty body, quiz stuck pending) instead of a silent blank
// screen — see the isEmptyContent handling below.
const EMPTY_OVERLAY_MESSAGE = {
    body: "Oups, un souci est survenu de notre côté. Tu peux fermer cette fenêtre et reprendre ton activité.",
    proposed_responses: [],
}

/**
 * SamConversationBase - Core conversation component for checkpoints
 *
 * @param {Object} props
 * @param {Object} props.conversation - Initial conversation data
 * @param {string} props.context - Conversation context ("raise_hand", "starting_block", etc.)
 * @param {Function} props.onClose - Callback when conversation is closed
 * @param {boolean} props.isOverlay - If true, renders as overlay; if false, renders as full screen
 * @param {Function} props.renderCustomButtons - Optional custom buttons to render
 * @param {boolean} props.showInitialLoading - Whether to show loading animation on mount
 * @param {Function} props.onStartingBlockComplete - Callback for starting block completion
 * @param {number} props.conversationWidth - Width for conversation bubble
 * @param {Object} props.containerStyle - Additional container styles
 * @param {Function} props.onShowCloseButtonChange - Callback when close button visibility changes
 * @param {Object} props.room - LiveKit room instance (optional, only for overlay)
 * @param {Function} props.setAttributesThrottled - LiveKit setAttributes function
 * @param {Function} props.triggerSamNotification - LiveKit event sender function
 * @param {boolean} props.showHeader - Whether to show header (default: true)
 * @param {boolean} props.showPetalBadge - Whether to show petal badge (default: true)
 */
const SamConversationBase = (props) => {
    const {
        conversation: initialConversation,
        context,
        onClose,
        isOverlay = false,
        renderCustomButtons,
        showInitialLoading = false,
        onStartingBlockComplete,
        conversationWidth: propConversationWidth,
        containerStyle = {},
        onShowCloseButtonChange,
        room = null,
        setAttributesThrottled = () => Promise.resolve(),
        triggerSamNotification = () => {},
        showHeader = true,
        showPetalBadge = true,
        // Legacy callbacks (kept for backwards compatibility)
        onResponseSubmit = () => {},
        onPetalEarned = () => {}
    } = props

    // Contexts and navigation
    const dimensions = useContext(DimensionsContext)
    const navigation = useNavigation()

    // Activity hooks
    const { currentActivity } = useCurrentActivity() || {}
    const [launchCameraCheckpoint] = useCamera(formIds.CHECKPOINT, true, false)
    const [launchCameraActivity] = useCamera(formIds.NEW_MATERIAL, true, false)
    const [launchCameraChat] = useCamera("chat", true, false)

    // Sound mode selector
    const soundModeIds = useSelector(state => state.liveMode?.soundModeIds)
    const respondConversationRequest = useSelector(state => state.api.respondConversation?.request)

    // Quiz-specific state
    const isQuiz = context === 'prerequisite_quiz' || initialConversation?.conversation?.trigger_context === 'prerequisite_quiz'
    const quizData = initialConversation?.quiz
    const quizConversationId = initialConversation?.conversation?.id
    const savedQuizProgress = props.quizProgress?.[quizConversationId]
    const [quizAnswers, setQuizAnswers] = useState(savedQuizProgress?.answers || [])
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(savedQuizProgress?.currentQuestionIndex || 0)
    const [quizConversationData, setQuizConversationData] = useState(initialConversation)
    const [quizLoading, setQuizLoading] = useState(false)
    const [quizResponseCount, setQuizResponseCount] = useState(savedQuizProgress?.answers?.length || 0)
    const [quizResultData, setQuizResultData] = useState(null)

    // Calculate conversation width
    const conversationWidth = propConversationWidth || (dimensions?.width || 0) - 40

    // Helper: check if context is starting block
    const isStartingBlock = isStartingBlockContext(context)

    // Get current activity
    const activity = useMemo(() => {
        if (isStartingBlock) return null

        if (currentActivity?.progressStatus === "ongoing") {
            return currentActivity?.activityData
        }
        return getMostRecentActivity(props.todo_activities) || null
    }, [currentActivity, isStartingBlock, props.todo_activities])

    // === REFACTORED HOOKS ===

    // State machine for conversation
    const { state, actions, computed } = useConversationState(initialConversation)

    // Timer for close button
    const canShowCloseButton = useCheckpointTimer(
        computed.isEnded,
        computed.isLoading,
        TIMING.CLOSE_BUTTON_DELAY
    )

    // LiveKit synchronization
    const {
        markCheckpointInactive,
        handleConversationComplete
    } = useLiveKitSync({
        room,
        setAttributesThrottled,
        triggerSamNotification,
        user: props.user,
        currentLiveSession: props.currentLiveSession
    })

    // Photo capture handling with Vapor upload
    const [pendingPhoto, setPendingPhoto] = useState(null)
    const [pendingPhotoIsCheckpoint, setPendingPhotoIsCheckpoint] = useState(false)

    // Prepare file for Vapor upload
    const filesToUpload = pendingPhoto ? [{
        uri: pendingPhoto?.uri || pendingPhoto,
        name: `checkpoint_photo_${props.user?.display_name}_${moment().format("YYYY-MM-DD_HHmmss")}.jpg`,
        type: "image/jpeg",
    }] : []

    // useRequestsBatch for Vapor upload then API call
    const [uploadProgress, uploading, startPhotoUpload] = useRequestsBatch(
        [], // no requests before upload
        filesToUpload,
        formIds.CHECKPOINT,
        {
            service: liveService.respondConversation,
            params: { id: computed.conversationId },
            data: {
                student_response: "photo prise par l'élève",
                ...(!pendingPhotoIsCheckpoint && activity?.id ? { activity: activity.id } : {})
            },
            tmpFilesKey: "checkpoint_attachments",
            tmpFilesFormatter: (s3Key) => s3Key, // Backend expects array of S3 key strings
            additionalData: [],
            onSuccessBeforeNext: () => {
                setPendingPhoto(null)
                setPendingPhotoIsCheckpoint(false)
                return true
            },
            onError: () => {
                setPendingPhoto(null)
                setPendingPhotoIsCheckpoint(false)
            },
            showErrorDialog: true,
        },
        [] // no requests after
    )

    // Start upload when photo is ready
    useEffect(() => {
        if (pendingPhoto && !uploading && computed.conversationId) {
            startPhotoUpload()
        }
    }, [pendingPhoto, uploading, computed.conversationId])

    const { clearProcessed, clearPhoto, expectChatPhoto, dropChatPhotoRequest } = usePhotoCapture({
        onPhotoTaken: useCallback((photo, isCheckpointPhoto) => {
            actions.submitResponse()
            setPendingPhotoIsCheckpoint(isCheckpointPhoto)
            setPendingPhoto(photo)
        }, [actions]),
        isStartingBlock,
        // A quiz never asks for a photo
        enabled: !isQuiz
    })

    // Sam's request for a chat photo lasts while the student is on the chat it opened:
    // back on this screen, the next chat photo is a message to the tutor again
    useEffect(() => navigation.addListener("focus", dropChatPhotoRequest), [navigation, dropChatPhotoRequest])

    // === EFFECTS ===

    // Track initialized conversation to prevent re-initialization on parent re-renders
    const initializedConversationIdRef = useRef(null)

    // Track which conversation already got its empty-overlay Bugsnag report
    const emptyOverlayNotifiedRef = useRef(null)

    // Initialize conversation (only once per conversation ID)
    useEffect(() => {
        if (!initialConversation) {
            return
        }

        // Get conversation ID to check if this is a new conversation
        const conversationId = initialConversation?.conversation?.id ||
                               initialConversation?.agent_message?.conversation_id

        // Check if this conversation was already initialized
        const wasAlreadyInitialized = conversationId && conversationId === initializedConversationIdRef.current

        // If already initialized, this is a Redux update for the same conversation
        // Only update data, don't re-initialize (prevents resetting state like isPetalEarned)
        if (wasAlreadyInitialized) {
            setQuizConversationData(initialConversation)

            // Only update state machine if conversation status has actually changed
            // This prevents resetting isAnimationComplete when Redux triggers re-render
            const currentStatus = state.conversation?.conversation?.status
            const newStatus = initialConversation?.conversation?.status
            if (initialConversation?.agent_message && isOverlay && currentStatus !== newStatus) {
                actions.receiveResponse({
                    conversation: initialConversation?.conversation,
                    agent_message: initialConversation?.agent_message
                })
            }
            return
        }

        // New conversation - mark as initialized
        initializedConversationIdRef.current = conversationId

        amplitude.track(`sam_${isOverlay ? "checkpoint_overlay" : "screen"}.open`)
        PlaySoundM.play(require("../../../assets/sounds/jingles_PIZZI14.mp3"))

        // Reset state for new conversation
        actions.init(initialConversation)
        clearProcessed()
        setQuizConversationData(initialConversation)

        // Set quiz response count - if resuming quiz, use number of saved answers
        if (savedQuizProgress && isQuiz) {
            setQuizResponseCount(savedQuizProgress.answers?.length || 0)
            setQuizAnswers(savedQuizProgress.answers || [])
            setCurrentQuestionIndex(savedQuizProgress.currentQuestionIndex || 0)
        } else {
            // Reset for new quiz
            setQuizResponseCount(0)
            setQuizAnswers([])
            setCurrentQuestionIndex(0)
        }

        // Show loading briefly then display
        if (showInitialLoading) {
            const timer = setTimeout(() => {
                if (isOverlay && initialConversation?.agent_message) {
                    actions.loaded(initialConversation.agent_message)
                }
            }, TIMING.INITIAL_LOADING_DELAY)
            return () => clearTimeout(timer)
        } else {
            if (initialConversation?.agent_message) {
                actions.loaded(initialConversation.agent_message)
            }
        }
    }, [initialConversation])

    // Notify parent of close button visibility changes
    useEffect(() => {
        onShowCloseButtonChange?.(canShowCloseButton)
    }, [canShowCloseButton, onShowCloseButtonChange])

    // Handle sound mode close - but NOT for prerequisite quizzes
    useEffect(() => {
        // Don't auto-close prerequisite quizzes in sound mode
        const isQuiz = context === 'prerequisite_quiz' ||
                       initialConversation?.conversation?.trigger_context === 'prerequisite_quiz'

        if (soundModeIds?.includes(props.user?.id) && !isQuiz) {
            handleClose()
        }
    }, [soundModeIds, props.user?.id, context, initialConversation?.conversation?.trigger_context])

    // === HANDLERS ===

    const handleTextSubmit = useCallback((item, textValue) => {
        actions.submitResponse()
        const response = item.action === RESPONSE_ACTIONS.TEXT
            ? `Écrire le nom du chapitre: ${textValue}`
            : `décrire le devoir : ${textValue}`

        props.apiRequest(
            liveService.respondConversation,
            { id: computed.conversationId },
            { student_response: response }
        )
        actions.clearTextInput()
    }, [computed.conversationId, actions, props.apiRequest])

    const handleDateSubmit = useCallback((item, dateValue) => {
        actions.submitResponse()
        props.apiRequest(
            liveService.respondConversation,
            { id: computed.conversationId },
            { student_response: `${item.body} - ${dateValue}` }
        )
    }, [computed.conversationId, actions, props.apiRequest])

    const handleClose = useCallback(() => {
        amplitude.track(`sam_${isOverlay ? "checkpoint_overlay" : "screen"}.close`)

        // Clear LiveKit attributes
        markCheckpointInactive()

        // End ongoing conversation
        if (computed.conversationId && state.conversation?.conversation?.status === "ongoing") {
            props.apiRequest(
                liveService.finishConversation,
                { conversation_id: computed.conversationId },
                { interruption_reason: "student_dismiss", ended_at: new Date().toISOString() }
            )
        }

        onClose?.({ isCompleted: computed.isEnded })
    }, [
        isOverlay, computed.conversationId, computed.isEnded,
        state.conversation?.conversation?.status,
        markCheckpointInactive, props.apiRequest, onClose
    ])

    // === QUIZ HANDLERS ===

    const moveToNextQuestion = useCallback((nextIndex) => {
        setQuizLoading(true)

        // Format next question
        const nextQuestion = quizData?.questions?.[nextIndex]
        if (!nextQuestion) return

        const questionText = nextQuestion.question_text || ''
        const options = nextQuestion.options || []

        const proposedResponses = options.map((option, index) => ({
            id: index + 1,
            body: option,
            quiz_question_id: nextQuestion.id,
            answer: option,
            user_signal: nextQuestion.user_signal || null,
        }))

        // Update conversation with next question
        const updatedConversation = {
            ...quizConversationData,
            agent_message: {
                id: `quiz_question_${nextQuestion.id}`,
                conversation_id: quizConversationData?.conversation?.id,
                body: questionText,
                proposed_responses: proposedResponses,
            }
        }

        setQuizConversationData(updatedConversation)
        setCurrentQuestionIndex(nextIndex)

        // Update the state machine with new message
        actions.loaded(updatedConversation.agent_message)

        // Small delay for better UX
        setTimeout(() => {
            setQuizLoading(false)
        }, 500)
    }, [quizData, quizConversationData, actions])

    const submitQuiz = useCallback(async (answers) => {
        setQuizLoading(true)

        try {
            // Get activity ID from current activity or props
            const activityId = activity?.id || currentActivity?.id || props.currentActivity?.id

            if (!activityId) {
                return
            }

            // Submit quiz answers with live_session_id for tutor notifications
            const response = await activitiesService.submitPrerequisiteQuizAttempt(
                { activityId },
                {
                    answers,
                    live_session_id: props.currentLiveSession?.id || null
                }
            )

            const attemptData = response?.data?.data || response?.data
            const backendConversation = response?.data?.conversation
            const quizResultPayload = response?.data?.quiz || null
            setQuizResultData(quizResultPayload)

            // Update conversation with results
            const passed = attemptData?.passed
            const score = attemptData?.score || 0
            const total = attemptData?.total || quizData?.questions?.length || 0
            // Calculate percentage if not provided by backend
            const percentage = attemptData?.percentage ?? (total > 0 ? (score / total) * 100 : 0)

            const resultMessage = passed
                ? `Bravo ! Tu as réussi le quiz avec ${score}/${total} (${Math.round(percentage)}%).\n\nTu peux maintenant commencer l'exercice ! 🎉`
                : `Tu as obtenu ${score}/${total} (${Math.round(percentage)}%).\n\nPas de souci, ton masteur va t'aider à réviser les notions nécessaires. 💪`

            const updatedConversation = {
                ...quizConversationData,
                conversation: backendConversation || {
                    ...quizConversationData?.conversation,
                    status: passed ? 'ended' : 'tutor_needed',
                    ended_at: new Date().toISOString(),
                },
                agent_message: {
                    id: 'quiz_result',
                    conversation_id: quizConversationData?.conversation?.id,
                    body: resultMessage,
                    proposed_responses: [],
                },
                quiz_result: attemptData,
            }

            // Award petal on quiz completion BEFORE updating Redux
            // This ensures isPetalEarned is true when component re-renders from Redux update
            if (!state.isPetalEarned) {
                actions.earnPetal()
                props.apiRequest(liveService.awardCheckpointPetal, { conversation_id: quizConversationData?.conversation?.id })
                onPetalEarned?.()
            }

            setQuizConversationData(updatedConversation)

            // Update the state machine with result - use receiveResponse so status becomes ENDED
            // This allows feedback buttons to show
            actions.receiveResponse({
                conversation: updatedConversation.conversation,
                agent_message: updatedConversation.agent_message
            })

            // CRITICAL: Update Redux checkpoint conversation with quiz result
            props.setCheckpointConversation(updatedConversation)

            // Clear quiz progress from Redux since quiz is complete
            if (quizConversationId) {
                props.clearQuizProgress(quizConversationId)
            }

            amplitude.track('quiz.completed', {
                passed,
                score,
                total,
                percentage,
                activity_id: activityId
            })

            // Notify tutor via LiveKit if in live session
            if (props.currentLiveSession?.tutor?.id && triggerSamNotification) {
                triggerSamNotification("agentConversationCompleted", {
                    liveSessionId: props.currentLiveSession.id,
                    studentId: props.user?.id,
                    tutor_id: props.currentLiveSession.tutor?.id,
                    displayName: props.user?.display_name,
                    conversation: updatedConversation,
                    timestamp: Date.now(),
                })
            }

        } catch (error) {
            console.error('[PrerequisiteQuiz] Failed to submit quiz:', error)

            // Show error message
            const errorMessage = {
                ...quizConversationData,
                conversation: {
                    ...quizConversationData?.conversation,
                    status: 'tutor_needed',
                },
                agent_message: {
                    id: 'quiz_error',
                    conversation_id: quizConversationData?.conversation?.id,
                    body: "Oups, une erreur s'est produite lors de la soumission du quiz. Ton masteur sera notifié pour t'aider. 🤝",
                    proposed_responses: [],
                },
            }

            setQuizConversationData(errorMessage)
            actions.loaded(errorMessage.agent_message)
        } finally {
            setQuizLoading(false)
        }
    }, [activity, currentActivity, props.currentActivity, props.currentLiveSession, quizData, quizConversationData, quizConversationId, triggerSamNotification, props.user, actions, props.setCheckpointConversation, props.clearQuizProgress])

    const handleQuizResponse = useCallback((response) => {
        // Store the answer
        const newAnswer = {
            quiz_question_id: response.quiz_question_id,
            answer: response.answer
        }
        const updatedAnswers = [...quizAnswers, newAnswer]
        setQuizAnswers(updatedAnswers)

        // Update response count for progress bar
        setQuizResponseCount(prev => prev + 1)

        // Check if there are more questions
        const nextIndex = currentQuestionIndex + 1
        if (quizData?.questions && nextIndex < quizData.questions.length) {
            // Save progress to Redux BEFORE moving to next question (in case of refresh)
            if (quizConversationId) {
                props.setQuizProgress(quizConversationId, updatedAnswers, nextIndex)
            }
            // Move to next question
            moveToNextQuestion(nextIndex)
        } else {
            // Submit quiz (progress will be cleared after submission)
            submitQuiz(updatedAnswers)
        }
    }, [quizAnswers, currentQuestionIndex, quizData, quizConversationId, props.setQuizProgress, moveToNextQuestion, submitQuiz])

    const handleResponseClick = useCallback((response) => {
        amplitude.track(`sam_${isOverlay ? "checkpoint_overlay" : "screen"}.response.click`, {
            action: response.action
        })
        PlaySoundM.play(require("../../../assets/sounds/select_002.mp3"))
        onResponseSubmit?.()

        // Handle quiz responses differently
        if (isQuiz && response.quiz_question_id) {
            handleQuizResponse(response)
            return
        }

        if (response.action === RESPONSE_ACTIONS.PHOTO) {
            clearPhoto()
            clearProcessed()

            if (isStartingBlock) {
                launchCameraCheckpoint?.()
            } else if (activity?.category === "write") {
                launchCameraActivity?.()
            } else {
                expectChatPhoto()
                navigation.push("messages", { userId: props.currentLiveSession?.tutor?.id })
                launchCameraChat?.()
            }
        } else if (response.action === RESPONSE_ACTIONS.ACTIVITY) {
            handleClose()
        } else {
            actions.submitResponse()
            props.apiRequest(
                liveService.respondConversation,
                { id: computed.conversationId },
                isStartingBlock
                    ? { student_response: response.body }
                    : { student_response: response.body, activity: activity?.id }
            )
        }
    }, [
        isOverlay, isStartingBlock, activity, computed.conversationId,
        launchCameraCheckpoint, launchCameraActivity, launchCameraChat,
        clearPhoto, clearProcessed, expectChatPhoto, actions, props.apiRequest, navigation,
        props.currentLiveSession?.tutor?.id, onResponseSubmit, isQuiz, handleQuizResponse, handleClose
    ])

    const handleFeedback = useCallback((value) => {
        if (state.feedbackValue !== null) return

        amplitude.track(`sam_${isOverlay ? "checkpoint_overlay" : "screen"}.feedback.submit`, {
            value
        })
        actions.giveFeedback(value)

        props.apiRequest(
            liveService.giveAgentFeedback,
            { id: computed.conversationId },
            { feedback_type: "student", feedback_value: value }
        )

        setTimeout(() => {
            handleClose()
            // For starting block, also trigger onStartingBlockComplete (like "C'est parti!" button)
            if (isStartingBlock && onStartingBlockComplete) {
                onStartingBlockComplete()
            }
        }, TIMING.FEEDBACK_CLOSE_DELAY)
    }, [state.feedbackValue, isOverlay, computed.conversationId, actions, props.apiRequest, isStartingBlock, onStartingBlockComplete, handleClose])

    // === API STATUS HANDLERS ===

    useApiStatus(
        liveService.respondConversation,
        null,
        false,
        (data) => {
            // receiveResponse already updates displayMessage, no need to call loaded() separately
            // Calling loaded() after receiveResponse caused a race condition where
            // isAnimationComplete would be reset to false after the animation completed
            actions.receiveResponse(data)

            // Handle suggested activity
            if (data?.conversation?.status === "todo_activity_launch" &&
                data?.conversation?.key_info?.next_todo_activity_id) {
                props.setSuggestedActivity?.(
                    data.conversation.live_session_id,
                    data.conversation.key_info.next_todo_activity_id
                )
            }

            // Award petal on completion
            if (isEndedStatus(data?.conversation?.status) && !state.isPetalEarned) {
                actions.earnPetal()
                props.apiRequest(
                    liveService.awardCheckpointPetal,
                    { conversation_id: data.conversation.id }
                )
                onPetalEarned?.()
            }

            // Handle LiveKit notifications
            handleConversationComplete(data)
        },
        () => {
            actions.setError()
            handleClose()
            markCheckpointInactive()
        }
    )

    useApiStatus(
        liveService.giveAgentFeedback,
        null,
        false,
        () => {
            setTimeout(() => {
                actions.giveFeedback(null)
                handleClose()
            }, 500)
        },
        () => actions.giveFeedback(null)
    )

    // === RENDER ===

    const messageToDisplay = isOverlay ? state.displayMessage : (state.displayMessage || initialConversation?.agent_message)
    const showLoading = computed.isLoading || respondConversationRequest || quizLoading

    // Render feedback buttons if appropriate
    const renderFeedbackButtons = () => {
        if (!computed.canShowFeedback) return null

        return (
            <FeedbackButtons
                value={state.feedbackValue}
                onFeedback={handleFeedback}
            />
        )
    }

    // Safety check: a conversation created or answered without an agent_message
    // (AI failure, malformed response) has no message body to show — same for a
    // quiz conversation still 'pending' before the student started it.
    const quizStatus = quizConversationData?.conversation?.status
    const conversationStatus = state.conversation?.conversation?.status
    const isQuizNotStarted = isQuizPendingWithoutQuestion({ message: messageToDisplay, isQuiz, quizStatus })
    const isEmptyContent = isEmptyCheckpointContent({ showLoading, message: messageToDisplay, isQuizNotStarted })

    // Inside the overlay, SamCheckpointOverlay paints a fullscreen background no
    // matter what we render here — silently returning null used to leave the
    // student behind an empty screen with no way out (prod has no other trace
    // of this, by construction). Report it once per conversation; the fallback
    // itself is rendered below, always closable immediately.
    useEffect(() => {
        if (!isEmptyContent || !isOverlay) return
        if (emptyOverlayNotifiedRef.current === computed.conversationId) return
        emptyOverlayNotifiedRef.current = computed.conversationId
        notifyBugsnag(new Error("Sam checkpoint overlay rendered empty"), {
            conversationId: computed.conversationId,
            context,
            conversationStatus,
            reason: isQuizNotStarted ? "quiz_not_started" : "no_agent_message",
        })
    }, [isEmptyContent, isOverlay, computed.conversationId, isQuizNotStarted, context, conversationStatus])

    // Outside the overlay (PreCallScreen), nothing paints over the screen, so
    // rendering nothing is harmless: keep the original silent contract.
    if (isEmptyContent && !isOverlay) {
        return null
    }

    const displayedMessage = isEmptyContent ? EMPTY_OVERLAY_MESSAGE : messageToDisplay

    return (
        <View style={[styles.conversationWrapper, containerStyle]}>
            {/* Header */}
            {showHeader && (
                <CheckpointHeader
                    showCloseButton={canShowCloseButton || isEmptyContent}
                    onClose={handleClose}
                    showPetalBadge={showPetalBadge && !isEmptyContent}
                    isPetalEarned={state.isPetalEarned}
                    progress={isQuiz ? quizResponseCount : state.responseCount}
                    width={conversationWidth}
                />
            )}

            {/* Conversation Bubble */}
            <ConversationBubble
                message={displayedMessage}
                isLoading={showLoading}
                width={conversationWidth}
                onAnimationComplete={actions.animationComplete}
                feedbackButtons={renderFeedbackButtons()}
            />

            {/* Response Options */}
            {state.isAnimationComplete && !showLoading && displayedMessage && (
                <Animated.View
                    exiting={FadeOut.duration(200)}
                    style={{ width: conversationWidth }}
                >
                    <ResponseOptions
                        options={displayedMessage?.proposed_responses}
                        onSelect={handleResponseClick}
                        onTextSubmit={handleTextSubmit}
                        onDateSubmit={handleDateSubmit}
                        textInput={state.textInput}
                        onTextInputChange={actions.setTextInput}
                        width={conversationWidth}
                    />

                    {/* Quiz Question Signal Button - Under the response options */}
                    {isQuiz && displayedMessage?.proposed_responses?.[0]?.quiz_question_id && (initialConversation?.conversation?.activity_id || activity?.id) && (
                        <View style={styles.signalButtonContainer}>
                            <QuizQuestionSignalButton
                                activityId={initialConversation?.conversation?.activity_id || activity?.id}
                                questionId={displayedMessage.proposed_responses[0].quiz_question_id}
                                context="during_quiz"
                            />
                        </View>
                    )}

                    {/* Inline quiz review + Continue button */}
                    {isQuiz && computed?.isEnded && (
                        <View style={{ alignItems: "center", marginTop: 16, width: "100%" }}>
                            <QuizCorrectionReview
                                data={quizResultData}
                                activityId={initialConversation?.conversation?.activity_id || activity?.id}
                                style={{ marginBottom: 12 }}
                            />
                            <FilledButtonM
                                label="Continuer"
                                onPress={handleClose}
                                color={theme.colors.primary}
                                style={{ marginTop: 4 }}
                            />
                        </View>
                    )}

                    {/* Custom buttons (for PreCallScreen "C'est parti" button) */}
                    {renderCustomButtons?.({
                        conversationData: state.conversation,
                        isTextAnimationComplete: state.isAnimationComplete,
                        onStartingBlockComplete,
                        performClose: handleClose
                    })}
                </Animated.View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    conversationWrapper: {
        width: "100%",
        justifyContent: "flex-start",
        alignItems: "center"
    },
    signalButtonContainer: {
        alignItems: "center",
        marginTop: 16,
        marginBottom: 8,
    },
})

const mapStateToProps = (state) => ({
    currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
        (x) => x?.id === state.liveMode?.liveSessionId
    ),
    user: state.data.currentUser?.object,
    todo_activities: state.data.currentUser?.object?.todo_activities,
    quizProgress: state.liveMode?.quizProgress
})

const mapDispatchToProps = {
    apiRequest,
    setCheckpointConversation,
    setQuizProgress,
    clearQuizProgress
}

export default connect(mapStateToProps, mapDispatchToProps)(SamConversationBase)
